// /app/frontend/src/components/innerwork/ShareToRoomModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { X, Users } from "lucide-react";
import { listRooms, createRoomPost } from "../../api/rooms";
import { shareReflection } from "../../api/innerWork";

function defaultSnippet(text, maxLen = 500) {
  const t = (text || "").trim();
  if (t.length <= maxLen) return t;
  return t.slice(0, maxLen).trimEnd() + "…";
}

export default function ShareToRoomModal({
  open,
  onClose,
  reflection,
  onShared,
}) {
  const [rooms, setRooms] = useState([]);
  const [roomSlug, setRoomSlug] = useState("");
  const [snippet, setSnippet] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  // Load rooms when modal opens
  useEffect(() => {
    if (!open) return;
    setErr("");
    setSending(false);
    setRoomSlug("");
    setSnippet(reflection?.content || reflection?.reflection_text || "");
    
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const r = await listRooms();
        if (mounted) setRooms(Array.isArray(r) ? r : []);
      } catch {
        // Silent fail
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [open, reflection]);

  const selectedRoom = useMemo(() => {
    return rooms.find(r => r.slug === roomSlug) || null;
  }, [rooms, roomSlug]);

  const canShare = useMemo(() => {
    return !!roomSlug && snippet.trim().length > 0 && !sending;
  }, [roomSlug, snippet, sending]);

  const doShare = async () => {
    if (!canShare || !selectedRoom) return;
    setErr("");
    setSending(true);
    try {
      // 1) Create room post
      const postText = `💭 From my Inner Work:\n\n${snippet.trim()}`;
      const post = await createRoomPost(roomSlug, postText);

      // 2) Record share (audit trail)
      await shareReflection(reflection.id, {
        share_type: "room_post",
        room_id: selectedRoom.id,
        snippet_text: snippet.trim(),
      });

      // Show success and close
      onShared?.({ room: selectedRoom, post });
      onClose?.();
      
      // Brief success toast (the parent can handle navigation)
      alert(`✓ Shared to ${selectedRoom.name}`);
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        "Could not share to room.";
      setErr(msg);
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-xl rounded-2xl surface-card p-5 shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-rose-500" />
              <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">Share to Community</span>
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Your full reflection is shared as written. You can edit it before posting.
            </div>
          </div>
          <button
            type="button"
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-600 dark:hover:text-gray-200 transition"
            onClick={onClose}
            disabled={sending}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Room Selector */}
        <div className="mt-4">
          <div className="text-xs font-medium text-gray-700">Choose a space</div>
          {loading ? (
            <div className="mt-2 text-sm text-gray-500">Loading rooms…</div>
          ) : rooms.length === 0 ? (
            <div className="mt-2 text-sm text-amber-600">No rooms available.</div>
          ) : (
            <select
              className="mt-1 w-full rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-gray-300 transition"
              value={roomSlug}
              onChange={(e) => setRoomSlug(e.target.value)}
              disabled={sending}
            >
              <option value="">— Select a room —</option>
              {rooms.map((r) => (
                <option key={r.id} value={r.slug}>
                  {r.name}
                </option>
              ))}
            </select>
          )}
          {selectedRoom && (
            <div className="mt-2 text-xs text-gray-500">{selectedRoom.description}</div>
          )}
        </div>

        {/* Snippet Editor */}
        <div className="mt-4">
          <div className="text-xs font-medium text-gray-700">Your post (editable)</div>
          <textarea
            className="mt-1 h-36 w-full resize-none rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-gray-300 transition"
            value={snippet}
            onChange={(e) => setSnippet(e.target.value)}
            disabled={sending}
            placeholder="Share a thought from your reflection…"
          />
          <div className="mt-1 text-[11px] text-gray-500">
            This will post as: &quot;💭 From my Inner Work: …&quot;
          </div>
        </div>

        {/* Error */}
        {err && <div className="mt-3 text-sm text-red-600">{err}</div>}

        {/* Actions */}
        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            type="button"
            className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
            onClick={onClose}
            disabled={sending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-xl bg-rose-500 px-5 py-2.5 text-sm font-medium text-white hover:bg-rose-600 disabled:opacity-50 transition"
            onClick={doShare}
            disabled={!canShare}
          >
            {sending ? "Sharing…" : "Share to Community"}
          </button>
        </div>
      </div>
    </div>
  );
}
